import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, from } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { SageIntegrationService } from '../../../shared/services/sage-integration.service';
import { AuthService } from '../../shared/services';
import { ToastService } from '../../shared/services/toast.service';
import {
  entityAccentForType,
  entityAccentSoftForType,
  OverviewBundle,
  SageCompany,
  SageEntityTypeName,
  sageErrorMessageFor,
  signInErrorMessageFor,
} from '../../../shared/services/sage-integration.model';

interface AnalysingStep {
  label: string;
  done: boolean;
}

interface InfoCard {
  entityType: SageEntityTypeName;
  icon: string;
  title: string;
}

@Component({
  selector: 'app-sage-connect',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sage-connect.component.html',
  styleUrls: ['./sage-connect.component.css'],
})
export class SageConnectComponent implements OnInit {
  fullName = '';
  tenantName = '';

  loadingStatus = true;
  connecting = false;
  errorMessage = '';

  // Reconnect of an already-set-up tenant: no picker, no analysing sequence, just a spinner while
  // the saved company is re-verified. Distinct from `connecting`, which drives the analysing card.
  reconnecting = false;

  // Set when a company is still linked but the connection is down, so the hero can say "Reconnect"
  // and name the company instead of reading as a first-time setup.
  previouslyLinkedCompany: string | null = null;

  // Only set when a reconnect found the saved company gone from the Sage account — picking a
  // different one invalidates every stored mapping, so the picker has to say so.
  missingCompanyWarning = '';

  autoThreshold = 92;
  reviewThreshold = 75;

  // Colors are derived from ENTITY_ROUTE_ORDER so they stay in sync with the entity accents used
  // elsewhere in Sage integration; the icons here are decorative and unrelated to any entity icon.
  readonly infoCards: InfoCard[] = [
    { entityType: 'SupplierCategory', icon: 'bi-diagram-3', title: 'What gets synced' },
    { entityType: 'ItemCategory', icon: 'bi-sliders', title: 'How records are matched' },
    { entityType: 'Item', icon: 'bi-signpost-split', title: 'What happens next' },
  ];
  readonly entityAccentForType = entityAccentForType;
  readonly entityAccentSoftForType = entityAccentSoftForType;

  analysingSteps: AnalysingStep[] = [
    { label: 'Authenticating with Sage', done: false },
    { label: 'Reading suppliers, categories and items', done: false },
    { label: 'Scoring names against AIVOR records', done: false },
    { label: 'Building your mapping worklist', done: false },
  ];

  // Company picker modal — sits between the OAuth callback and the analysing sequence.
  showCompanyPicker = false;
  companiesLoading = false;
  companiesError = '';
  companies: SageCompany[] = [];
  selectedCompanyId: string | null = null;
  existingSageCompanyId: string | null = null;
  savingCompany = false;
  saveCompanyError = '';

  constructor(
    private sageService: SageIntegrationService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const jwt = localStorage.getItem('authToken');
    if (jwt) {
      try {
        const decoded: any = jwtDecode(jwt);
        this.fullName = decoded.FullName || '';
        this.tenantName = decoded.TenantName || '';
      } catch {
        /* invalid token — leave fields blank */
      }
    }

    const sageParam = this.route.snapshot.queryParamMap.get('sage');
    if (sageParam === 'connected') {
      this.resumeAfterOAuth();
      return;
    }
    // Carries on to the status check below, so a returning organisation still sees "Reconnect".
    const signInFailed = sageParam === 'error';
    if (signInFailed) {
      this.errorMessage = signInErrorMessageFor(this.route.snapshot.queryParamMap.get('reason'));
    }
    if (sageParam === 'disconnected') {
      this.errorMessage = 'Your Sage connection needs to be reconnected.';
    }

    this.sageService.getStatus().subscribe({
      next: (status) => {
        this.loadingStatus = false;
        if (status.connected) {
          this.routeToDestination();
          return;
        }
        // A disconnected tenant keeps its company link, so this tells a returning user their
        // setup survived rather than showing them a bare first-time connect screen.
        this.previouslyLinkedCompany = status.sageCompanyName || status.sageCompanyId || null;
      },
      error: (err: HttpErrorResponse) => {
        this.loadingStatus = false;
        if (err.status === 401) {
          // Session expired — same convention as the rest of the app (see
          // main-dashboard.component.ts) rather than silently showing the hero card
          // as if Sage were simply disconnected.
          this.authService.logOut();
          return;
        }
        if (!signInFailed) {
          this.errorMessage = "We couldn't check your Sage connection. Please check your connection and try again.";
        }
      },
    });
  }

  onConnectClick(): void {
    this.connecting = true;
    this.errorMessage = '';
    this.sageService.connect().subscribe({
      next: (res) => {
        window.location.href = res.authorizeUrl;
      },
      error: () => {
        this.connecting = false;
        this.errorMessage = 'Could not start the Sage connection. Please try again.';
      },
    });
  }

  // Stays non-fatal — the Items screen copes with an empty report-group list — but an expired
  // token or a rate-limit block surfaces rather than vanishing behind the onboarding redirect.
  private warnOnWarmUpFailure(err: unknown): void {
    const message = sageErrorMessageFor(err);
    if (message) this.toast.warning(message);
  }

  // The OAuth callback lands here after every connect, including a reconnect by a tenant who already
  // picked a company and finished onboarding. Re-picking the same company and sitting through the
  // analysing sequence again is pure friction, so reuse the saved company when it still exists and
  // hand straight over to routeToDestination().
  private resumeAfterOAuth(): void {
    this.loadingStatus = false;
    this.reconnecting = true;

    this.sageService.getStatus().subscribe({
      next: (status) => {
        // Company fields are returned even while disconnected, so connectedness has to be checked
        // before the saved company can be trusted — otherwise a failed reconnect would be waved
        // through to the console and bounced straight back.
        if (!status.connected) {
          this.reconnecting = false;
          this.errorMessage = 'We could not confirm the connection to Sage. Please try again.';
          return;
        }

        const savedCompanyId = status.sageCompanyId || null;
        this.existingSageCompanyId = savedCompanyId;

        if (!savedCompanyId) {
          this.reconnecting = false;
          this.openCompanyPicker();
          return;
        }

        this.sageService.getCompanies().subscribe({
          next: (companies) => {
            if (companies.some((c) => String(c.id) === savedCompanyId)) {
              // runAnalysingSequence is skipped on this path, so its report-group warm-up has to
              // happen here. Fire-and-forget: the Items screen falls back to an empty list.
              this.sageService.refreshItemReportGroups().subscribe({
                error: (err) => this.warnOnWarmUpFailure(err),
              });
              // Onboarding is already complete for almost every reconnect, so the bundle
              // short-circuits before touching entity data — cheap to prefetch either way.
              this.sageService.getOverviewBundle().subscribe();
              this.routeToDestination();
              return;
            }
            // Gone from the Sage account — deleted, or a different Sage login was used. Every
            // stored mapping points at the old company, so picking another one invalidates them.
            this.reconnecting = false;
            this.missingCompanyWarning =
              `${status.sageCompanyName || savedCompanyId} is no longer available in this Sage ` +
              'account. Choosing a different company means re-reviewing all your mappings.';
            this.openCompanyPicker(companies);
          },
          error: () => {
            // Can't prove the saved company is still there, so never skip — the picker re-fetches
            // and owns the retry banner.
            this.reconnecting = false;
            this.openCompanyPicker();
          },
        });
      },
      error: () => {
        this.reconnecting = false;
        this.openCompanyPicker();
      },
    });
  }

  openCompanyPicker(prefetched?: SageCompany[]): void {
    this.showCompanyPicker = true;
    if (prefetched) {
      // Reusing the list resumeAfterOAuth already fetched — a second GET /Company/Get here would
      // spend one of the tenant's 100 requests/minute for nothing.
      this.companies = prefetched;
      this.companiesLoading = false;
      if (prefetched.length === 1) {
        this.selectedCompanyId = String(prefetched[0].id);
      }
      return;
    }
    this.loadCompanies();
  }

  loadCompanies(): void {
    this.companiesLoading = true;
    this.companiesError = '';
    this.sageService.getStatus().subscribe({
      next: (status) => (this.existingSageCompanyId = status.sageCompanyId || null),
      error: () => {
        /* non-fatal — the "already linked" pill is best-effort */
      },
    });
    this.sageService.getCompanies().subscribe({
      next: (companies) => {
        this.companiesLoading = false;
        this.companies = companies;
        // Exactly one company: still require an explicit Continue click rather than
        // auto-saving, so the CompanyId is an auditable choice, not an invisible default.
        if (companies.length === 1) {
          this.selectedCompanyId = String(companies[0].id);
        } else if (this.existingSageCompanyId) {
          this.selectedCompanyId = this.existingSageCompanyId;
        }
      },
      error: () => {
        this.companiesLoading = false;
        this.companiesError = 'We could not fetch your Sage companies. Please try again.';
      },
    });
  }

  selectCompanyOption(company: SageCompany): void {
    this.selectedCompanyId = String(company.id);
  }

  isAlreadyLinked(company: SageCompany): boolean {
    return this.existingSageCompanyId != null && this.existingSageCompanyId === String(company.id);
  }

  cancelCompanyPicker(): void {
    // Token is kept, SageCompanyId stays null — re-entering the connect flow reopens this picker.
    this.showCompanyPicker = false;
    this.saveCompanyError = '';
  }

  confirmCompanySelection(): void {
    if (!this.selectedCompanyId || this.savingCompany) return;
    this.savingCompany = true;
    this.saveCompanyError = '';
    const selectedCompanyName = this.companies.find((c) => String(c.id) === this.selectedCompanyId)?.name;
    this.sageService.selectCompany(this.selectedCompanyId, selectedCompanyName).subscribe({
      next: () => {
        this.savingCompany = false;
        this.showCompanyPicker = false;
        this.runAnalysingSequence();
      },
      error: (err: HttpErrorResponse) => {
        this.savingCompany = false;
        this.saveCompanyError = err?.error?.message || 'Could not save your company selection. Please try again.';
      },
    });
  }

  private runAnalysingSequence(): void {
    this.loadingStatus = false;
    this.connecting = true;

    // Pulls GL reporting groups into the local cache so the Items screen has them ready.
    // Fired once here, in parallel with the cosmetic tick sequence — a failure shouldn't block onboarding.
    this.sageService.refreshItemReportGroups().subscribe({
      error: (err) => this.warnOnWarmUpFailure(err),
    });

    // Fetches everything the overview screen needs (status, onboarding state, per-entity
    // sessions) in parallel with the cosmetic tick sequence below, and caches it — this is the
    // work that used to happen only after navigating to overview, showing as a blank screen.
    const bundle$ = this.sageService.getOverviewBundle();

    let i = 0;
    const ticksDone = new Promise<void>((resolve) => {
      const tick = () => {
        if (i < this.analysingSteps.length) {
          this.analysingSteps[i].done = true;
          i++;
          setTimeout(tick, 650);
        } else {
          resolve();
        }
      };
      setTimeout(tick, 650);
    });

    forkJoin([bundle$, from(ticksDone)]).subscribe({
      next: ([bundle]) => this.proceedFromBundle(bundle),
      error: () => {
        this.connecting = false;
        this.errorMessage = 'We could not confirm the connection to Sage. Please try again.';
      },
    });
  }

  // Once onboarding is complete the wizard retires for this tenant and the console takes over.
  private proceedFromBundle(bundle: OverviewBundle): void {
    if (!bundle.status.connected) {
      this.connecting = false;
      this.errorMessage = 'We could not confirm the connection to Sage. Please try again.';
      return;
    }
    this.router.navigate([
      bundle.onboardingCompleted ? '/sage-integration/console' : '/sage-integration/overview',
    ]);
  }

  // Once onboarding is complete the wizard retires for this tenant and the console takes over.
  private routeToDestination(): void {
    this.sageService.getOnboardingState().subscribe({
      next: ({ completed }) =>
        this.router.navigate([
          completed ? '/sage-integration/console' : '/sage-integration/overview',
        ]),
      // First-ever connection or a transient failure — fall back to the overview, which already
      // handles starting fresh sessions per entity.
      error: () => this.router.navigate(['/sage-integration/overview']),
    });
  }
}
